/**
 * @copyright Copyright 2019 Epic Systems Corporation
 * @file useTrackerRefs internal helper function
 * @author Roland Scott
 * @module react-async-hook\internal\use-async\use-tracker-refs
 */

import { useRef, useMemo } from "react";
import { noOp } from "./no-op";
import { IAsyncFunction, IAction } from "../";
import { Executor, Ignorer } from "../../";
import { AsyncPlaceholder, IAsyncState, IAsyncPlaceholderProps } from "../../types";
import {
	IsInitializedRef,
	ExecutorRef,
	IgnorerRef,
	StateUpdaterRef,
	CallbackExecutorRef,
	NextIdRef,
	AsyncFinishersRef,
	CleanupRef,
	StateRef,
	WrappedPlaceholderRef,
} from "../types";

type TrackerRefs<TAsync extends IAsyncFunction, TPlaceholderProps extends IAsyncPlaceholderProps> = [
	IsInitializedRef,
	ExecutorRef<TAsync>,
	IgnorerRef,
	StateUpdaterRef,
	CallbackExecutorRef,
	NextIdRef,
	AsyncFinishersRef,
	CleanupRef,
	StateRef<TAsync>,
	WrappedPlaceholderRef<TPlaceholderProps>,
];

/**
 * Grab all the refs we need to build the async tracker.
 */
export function useTrackerRefs<
	TAsync extends IAsyncFunction,
	TPlaceholderProps extends IAsyncPlaceholderProps
>(): TrackerRefs<TAsync, TPlaceholderProps> {
	// Refs are like instance variables for your functional component.
	// They are torn down when the component is unmounted.
	const isInitializedRef = useRef(false);
	const executeRef = useRef<Executor<TAsync>>(noOp as any);
	const ignoreRef = useRef<Ignorer>(noOp);
	const updateStateRef = useRef<(action: IAction) => void>(noOp);
	const executeCallbacksRef = useRef<(result: any, error: any) => void>(noOp);
	const nextIdRef = useRef(0);
	const asyncFinishersRef = useRef<{ [id: string]: (result: any, error: any) => void }>({});
	const cleanupRef = useRef<() => void>(undefined as any);
	const stateRef = useRef<IAsyncState>(undefined as any);
	const wrappedPlaceholderRef = useRef<AsyncPlaceholder<TPlaceholderProps>>(undefined as any);
	// Memoize the returned array/tuple. The refs will never change, so memozie with [].
	const retVal = useMemo<TrackerRefs<TAsync, TPlaceholderProps>>(
		() => [
			isInitializedRef,
			executeRef,
			ignoreRef,
			updateStateRef,
			executeCallbacksRef,
			nextIdRef,
			asyncFinishersRef,
			cleanupRef,
			stateRef,
			wrappedPlaceholderRef,
		],
		[],
	);
	return retVal;
}
